/**
 * @fileoverview Input/output schemas for the c-storyline-extractor-v1 agent.
 *
 * This agent extracts C-Stories (tangential storylines that add breadth
 * and relief) using the A-Story timeline as a reference anchor.
 *
 * Output schema is flat (3 levels max) for Gemini API compatibility.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { StructurePhaseSchema } from '@/domains/story-planning/schemas/structure-phases';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

export const InputSchema = BaseFlowInputSchema.extend({
  synopsisInput: z.string().describe('The condensed synopsis or full text to analyze.'),
  isFullText: z.boolean().optional().describe('When true, the synopsisInput contains the full story text rather than a condensed synopsis.'),
  aStoryContext: z.string().optional().describe(
    'JSON string of the A-Story extraction output (storyline name, summary, beats). ' +
    'Provides the main narrative timeline as a reference anchor for mapping C-Story beats.'
  ),
  storyType: z.string().optional().describe('The story type (e.g., "Novelette", "Novel", "Epic"). Used to calibrate storyline beat density.'),
  projectedWordCount: z.number().optional().describe('The target word count for the story. Used to calibrate storyline beat density.'),
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA (Flat structure for Gemini compatibility)
// =============================================================================

/**
 * A C-Story: a tangential storyline for breadth or relief.
 */
const StorylineOutputSchema = z.object({
  name: z.string().describe(
    'Short, evocative name for the C-Story (2-4 words). ' +
    'Named after the subplot focus ("The Ewok Alliance", "Comic Relief Duo", "The Smuggler\'s Debt").'
  ),
  summary: z.string().describe(
    'A brief description of the C-Story (1-3 sentences). ' +
    'Captures what this tangential thread adds to the story.'
  ),
  priority: z.literal('C').describe(
    'Always "C" — this agent only extracts tangential storylines.'
  ),
});

/**
 * Beat with storylineIndex reference.
 * C-Stories are lighter — beat density scaled to story type.
 */
const BeatOutputSchema = z.object({
  storylineIndex: z.number().describe('Index of the C-Story this beat belongs to (0-based, referencing the storylines array).'),
  storylinePhase: StructurePhaseSchema.describe(
    'The structural function this beat serves for THIS C-STORY\'s internal arc. ' +
    'A C-Story may only span a few phases — its "Initial State" might not occur until the global Midpoint.'
  ),
  globalPhase: StructurePhaseSchema.describe(
    'Where this beat falls in the OVERALL story timeline, anchored by the A-Story. ' +
    'C-Stories often weave in and out of the timeline, so globalPhase may differ from storylinePhase.'
  ),
  plotEvent: z.string().describe(
    'The concrete event in this tangential storyline. ' +
    'Can be action, comedy, world-building, or any moment that adds texture to the narrative.'
  ),
  narrativeState: z.string().optional().describe(
    'The tone or thematic contribution of this moment (optional).'
  ),
  goalProgress: z.number().min(-1).max(1).describe(
    'Progress toward this C-Story\'s resolution. ' +
    '+1 = Advancement. -1 = Setback. 0 = Neutral.'
  ),
});

/**
 * Story question for a C-Story.
 */
const StoryQuestionOutputSchema = z.object({
  storylineIndex: z.number().describe('Index of the C-Story this question belongs to (0-based).'),
  question: z.string().describe('The dramatic question for this tangential thread (e.g., "Will the smuggler pay off his debt?")'),
  status: z.enum(['open', 'answered']).default('open').describe('Whether this question is still open or has been answered in the text'),
});

/**
 * Output schema for the c-storyline-extractor-v1 agent.
 *
 * Flat structure (3 levels max) for Gemini API compatibility.
 * Contains zero or more C-Stories with concise beat coverage.
 */
export const OutputSchema = z.object({
  storylines: z.array(StorylineOutputSchema).describe(
    'C-Stories extracted from the text. These are tangential threads that add breadth or relief.'
  ),
  beats: z.array(BeatOutputSchema).describe(
    'Beats for C-Stories across structural phases. ' +
    'Beat count per phase should match the story type — see prompt guidance.'
  ),
  storyQuestions: z.array(StoryQuestionOutputSchema).describe(
    'Dramatic questions for C-Stories.'
  ),
});

export type Output = z.infer<typeof OutputSchema>;

// Type exports for consumer convenience
export type StorylineOutput = z.infer<typeof StorylineOutputSchema>;
export type BeatOutput = z.infer<typeof BeatOutputSchema>;
export type StoryQuestionOutput = z.infer<typeof StoryQuestionOutputSchema>;
